import { Link } from "react-router-dom";
import { ArrowRight, CalendarCheck, MapPin, Receipt, Sparkles } from "lucide-react";
import { euro } from "@/lib/api";
import { EXTREME } from "@/lib/subscription";
import { trackSubCta } from "@/components/subscription/SubOffers";

const cta = "group inline-flex h-14 items-center justify-center gap-2 rounded-full bg-[var(--gold)] px-8 text-sm font-bold uppercase tracking-[0.2em] text-[var(--ink)] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[var(--gold-soft)] active:scale-[0.98]";

export function ExtremeCta({ placement, to = `${EXTREME.path}#souscrire`, testid, className = "" }) {
  return (
    <Link to={to} onClick={() => trackSubCta(placement, "extreme")} data-testid={testid} className={`${cta} ${className}`}>
      Je m'abonne <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
    </Link>
  );
}

/** Bloc prix : produit et livraison affichés séparément, puis le total mensuel. */
export function ExtremePrice({ testid = "extreme-price", big = false }) {
  return (
    <div data-testid={testid}>
      <p className={`font-display leading-none text-[var(--gold-soft)] ${big ? "text-6xl md:text-7xl" : "text-5xl"}`}>
        <span data-testid={`${testid}-product`}>{euro(EXTREME.price)}</span><span className="text-xl text-[var(--parchment)]/80"> / mois</span>
      </p>
      <p data-testid={`${testid}-shipping`} className="mt-3 text-base text-[var(--parchment)]">Livraison en point relais : <b>{euro(EXTREME.shipping)}</b>.</p>
      <p data-testid={`${testid}-total`} className="mt-1 text-base text-[var(--parchment)]/90">Soit <b className="text-[var(--gold-soft)]">{euro(EXTREME.total)} par mois</b>, livraison comprise.</p>
    </div>
  );
}

function BottleVisual({ className = "" }) {
  return (
    <div className={`relative overflow-hidden rounded-sm bg-[var(--plum-deep)] ${className}`}>
      <img src={EXTREME.image} alt="Flacon Extrême Repouss 50 ml Ozentya" width="848" height="1264" loading="lazy" decoding="async" className="h-full w-full object-cover" />
      <span className="absolute left-4 top-4 rounded-full bg-[var(--gold)] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-[var(--ink)]">Nouveau · Abonnement</span>
    </div>
  );
}

/** Grande bannière commerciale (page dédiée et accueil). */
export function ExtremeBanner({ placement, testid = "extreme-banner", headingLevel = "h2", ctaTo }) {
  const H = headingLevel;
  return (
    <div data-testid={testid} className="grid overflow-hidden rounded-sm border border-[var(--gold)]/50 bg-[var(--ink-2)] md:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
      <BottleVisual className="aspect-square sm:aspect-[4/5] md:aspect-auto md:min-h-[560px]" />
      <div className="flex flex-col justify-center p-6 sm:p-10 lg:p-14">
        <p className="eyebrow mb-5">L'Abonnement Extrême Repouss</p>
        <H className="font-display text-4xl leading-[1.02] sm:text-5xl lg:text-6xl">EXTRÊME REPOUSS <span className="text-[var(--gold-soft)]">— 50 ML</span></H>
        <p className="mt-4 font-display text-2xl italic text-[var(--parchment)]/90 md:text-3xl">Votre rituel beauté, chaque mois.</p>
        <div className="mt-8 border-t border-[var(--line)] pt-8"><ExtremePrice testid={`${testid}-price`} big /></div>
        <div className="mt-8"><ExtremeCta placement={placement} to={ctaTo} testid={`${testid}-cta`} className="w-full sm:w-auto" /></div>
        <p data-testid={`${testid}-mention`} className="mt-5 text-sm leading-relaxed text-[var(--parchment)]/85">Recevez votre Extrême Repouss chaque mois directement dans le point relais de votre choix.</p>
        <p className="mt-2 text-xs text-[var(--parchment)]/75">Mondial Relay en France et en Europe · Sans engagement : pause ou résiliation depuis « Mon abonnement ».</p>
      </div>
    </div>
  );
}

const BENEFITS = [
  { n: "01", icon: Sparkles, title: "Un petit prix chaque mois", text: `Le produit à ${euro(EXTREME.price)} par mois, avec une tarification claire et transparente.` },
  { n: "02", icon: CalendarCheck, title: "Votre produit livré régulièrement", text: "Recevez votre flacon de 50 ml chaque mois, expédié après chaque prélèvement mensuel." },
  { n: "03", icon: MapPin, title: "Une livraison pratique", text: `Choisissez un point relais Mondial Relay en France ou en Europe : ${euro(EXTREME.shipping)} par envoi.` },
  { n: "04", icon: Receipt, title: "Un abonnement sans surprise", text: `${euro(EXTREME.total)} par mois au total. Renouvellement, fréquence et résiliation sont affichés avant le paiement.` },
];

export function ExtremeBenefits() {
  return (
    <div data-testid="extreme-benefits" className="grid gap-px overflow-hidden rounded-sm border border-[var(--line)] bg-[var(--line)] sm:grid-cols-2 lg:grid-cols-4">
      {BENEFITS.map(({ n, icon: Icon, title, text }) => (
        <div key={n} data-testid={`extreme-benefit-${n}`} className="bg-[var(--ink)] p-6 md:p-8">
          <div className="flex items-center justify-between"><span className="font-display text-3xl text-[var(--gold-soft)]">{n}</span><Icon className="h-6 w-6 text-[var(--gold)]" strokeWidth={1.4} /></div>
          <h3 className="mt-6 font-display text-2xl leading-tight">{title}</h3>
          <p className="mt-3 text-sm leading-relaxed text-[var(--parchment)]/85">{text}</p>
        </div>
      ))}
    </div>
  );
}

/** Accueil : bannière + avantages, sans pop-up ni animation. */
export function ExtremeHomeSection() {
  return (
    <section data-testid="home-extreme-section" className="px-5 pt-16 md:px-10">
      <div className="mx-auto max-w-6xl">
        <ExtremeBanner placement="home" testid="home-extreme-banner" />
        <Link to={EXTREME.path} data-testid="home-extreme-details-link" className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[var(--gold-soft)] underline underline-offset-4">Tout savoir sur l'abonnement <ArrowRight className="h-4 w-4" /></Link>
      </div>
    </section>
  );
}

/** Catalogue et fiches Extrême Repouss : encart compact. */
export function ExtremePromo({ placement, testid, className = "mt-8" }) {
  return (
    <Link to={EXTREME.path} onClick={() => trackSubCta(placement, "extreme")} data-testid={testid}
      className={`group flex items-center gap-4 rounded-sm border-2 border-[var(--gold)] bg-[rgba(217,180,106,0.08)] p-4 transition-colors hover:bg-[rgba(217,180,106,0.14)] ${className}`}>
      <img src={EXTREME.image} alt="" aria-hidden="true" width="64" height="96" loading="lazy" className="h-24 w-16 shrink-0 rounded-sm object-cover" />
      <span className="flex-1 text-sm">
        <span className="block text-xs font-bold uppercase tracking-[0.18em] text-[var(--gold-soft)]">Nouveau · L'Abonnement Extrême Repouss</span>
        <span className="mt-1 block font-display text-xl leading-tight">Le flacon 50 ml chaque mois : {euro(EXTREME.price)} / mois</span>
        <span className="block text-xs text-[var(--parchment)]/85">+ {euro(EXTREME.shipping)} de livraison en point relais, soit {euro(EXTREME.total)} par mois · sans engagement</span>
      </span>
      <ArrowRight className="h-5 w-5 shrink-0 text-[var(--gold-soft)] transition-transform group-hover:translate-x-1" />
    </Link>
  );
}
